import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { storyblokInit, apiPlugin } from "gatsby-source-storyblok"
import { useStaticQuery, graphql } from "gatsby"

//layout parts
import Header from "../parts/header/header"
import Footer from "../parts/footer/footer"

//blocks
import CTABanner from "../blocks/cta_banner/cta_banner"
import FlexColumns from "../blocks/flex_columns/flex_columns"
import Grid from "../blocks/grid/grid"
import HeroBanner from "../blocks/hero_banner/hero_banner"
import LogoGrid from "../blocks/logo_grid/logo_grid"
import NewsQuery from "../blocks/news_query/news_query"
import PageTitle from "../blocks/page_title/page_title"
import PartnerQuery from "../blocks/partner_query/partner_query"
import PlatformAnimation from "../blocks/platform-animation/PlatformAnimation"
import PoweredByZignalQuery from "../blocks/powered_by_zignal_query/powered_by_zignal_query"
import Quote from "../blocks/quote/quote"
import Scroller from "../blocks/scroller/scroller"
import Form from "../components/form/form"
import Standard from "../blocks/standard/standard"
import TeamMembersQuery from "../blocks/team_members_query/team_members_query"
import OpenPositions from "../blocks/open_positions/open_positions"

//components
import BackgroundColor from "../blocks/parts/background/backgroundColor"
import BackgroundOrb from "../blocks/parts/background/backgroundOrb"
import BackgroundImage from "../blocks/parts/background/backgroundImage"
import BackgroundVideo from "../blocks/parts/background/backgroundVideo"
import Button from "../components/button/button"
import ColumnItem from "../blocks/parts/column_item/column_item"
import FlexColumnsComponent from "../components/flex_columns_component/flex_columns_component"
import GridItem from "../blocks/parts/grid_item/grid_item"
import HoverCard from "../components/hover_card/hover_card"
import Html from "../components/html/html"
import IconAnimated from "../components/icon_animated/icon_animated"
import Image from "../components/image/image"
import InsightCard from "../components/insight_card/insight_card"
import ParentMenuItem from "../components/parent_menu_item/parent_menu_item"
import Partner from "../blocks/parts/partner/partner"
import Position from "../blocks/parts/position/position"
import PostCard from "../blocks/parts/post_card/post_card"
import Spacer from "../components/spacer/spacer"
import SubMenuItem from "../components/sub_menu_item/sub_menu_item"
import Svg from "../components/icon_svg/icon_svg"
import Text from "../components/text/text"
import MapAnimation from "../components/map_animation/map_animation"

import gsap from "gsap"
import ScrollTrigger from "gsap/ScrollTrigger"
import classNames from "classnames"

import * as Sentry from "@sentry/gatsby";
Sentry.init({
  dsn: "https://d397ca4f0c56738d8fe46575b82793f8@o4507273134866432.ingest.us.sentry.io/4507273138012160",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

storyblokInit({
  accessToken: process.env.GATSBY_STORYBLOK_API_TOKEN,
  use: [apiPlugin],
  components: {
    //parts
    header: Header,
    footer: Footer,

    //blocks
    cta_banner: CTABanner,
    flex_columns: FlexColumns,
    grid: Grid,
    hero_banner: HeroBanner,
    news_query: NewsQuery,
    page_title: PageTitle,
    partner_query: PartnerQuery,
    platform_animation: PlatformAnimation,
    powered_by_zignal_query: PoweredByZignalQuery,
    quote: Quote,
    scroller: Scroller,
    form_embed: Form,
    standard: Standard,
    team_members_query: TeamMembersQuery,
    open_positions: OpenPositions,

    //components
    background_color: BackgroundColor,
    background_orb: BackgroundOrb,
    background_image: BackgroundImage,
    background_video: BackgroundVideo,
    button: Button,
    column_item: ColumnItem,
    flex_columns_component: FlexColumnsComponent,
    grid_item: GridItem,
    hover_card: HoverCard,
    html: Html,
    icon_animated_new: IconAnimated,
    icon_svg: Svg,
    image: Image,
    insight_card: InsightCard,
    logo_grid: LogoGrid,
    parent_menu_item: ParentMenuItem,
    partner: Partner,
    post_card: PostCard,
    spacer: Spacer,
    sub_menu_item: SubMenuItem,
    text: Text,
    map_animation: MapAnimation,
    position: Position,
  },
})

const Layout = ({ children, pageInfo }) => {
  const [isEditor, setIsEditor] = useState(false)

  //Let's bring in the global settings.
  const global = useStaticQuery(
    graphql`
      query {
        storyblokEntry(slug: { eq: "global-settings" }) {
          content
          name
        }
      }
    `
  )
  const data = JSON.parse(global.storyblokEntry.content)
  data.site = global.site

  useEffect(() => {
    if (window.parent.location !== window.location) {
      // You are in Draft Mode or in the Visual Editor. Skip animation.
      setIsEditor(true)
      return
    }

    let ctx = gsap.context(() => {
      gsap.registerPlugin(ScrollTrigger)

      // Image reveal.
      ScrollTrigger.batch(".gs_reveal_img", {
        onEnter: images => gsap.set(images, { stagger: 0.2, attr: { in: 1 } }),
        onLeaveBack: images => gsap.set(images, { attr: { in: 0 } }),
      })

      // Content reveal.
      ScrollTrigger.batch(".gs_reveal", {
        onEnter: el => gsap.set(el, { stagger: 0.2, attr: { in: 1 } }),
        onLeaveBack: el => gsap.set(el, { attr: { in: 0 } }),
      })
    })
    return () => ctx.revert()
  }, [])

  return (
    <main
      className={classNames("main", {
        "visual-editor": isEditor,
        [`page_${pageInfo.slug}`]: pageInfo?.slug,
        [pageInfo.field_component]: pageInfo?.field_component,
      })}
    >
      <Header data={data}></Header>
      <div className="main-content">{children}</div>
      <Footer data={data}></Footer>
    </main>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
